import { useQuery } from '@tanstack/react-query'
import { http } from '@/lib/http'
import type { CityRecord, Country } from '@/types/models'

export const geoApi = {
  /** Public, sorted by name; the whole list in one page as the mobile app does. `launched` lists only countries LeazePro is launched in (LP-923). */
  countries: (opts?: { launched?: boolean }) =>
    http
      .get<{ items?: Country[]; data?: Country[] } | Country[]>('/geo-records/countries', {
        params: { page: 1, size: 1000, sortBy: 'name', sortOrder: 1, ...(opts?.launched ? { launched: true } : {}) },
        skipAuth: true,
      })
      .then(unwrapList),
  /** GET /geo-records/search-cities?searchTerm= — city + state + country codes for the manual address form (2+ characters). */
  searchCities: (searchTerm: string) =>
    http.get<{ items?: CityRecord[]; data?: CityRecord[] } | CityRecord[]>('/geo-records/search-cities', { params: { page: 1, size: 20, sortBy: 'name', sortOrder: 1, searchTerm } }).then((d) => unwrapList<CityRecord>(d)),
}

function unwrapList<T>(data: { items?: T[]; data?: T[] } | T[]): T[] {
  if (Array.isArray(data)) return data
  return data.items ?? data.data ?? []
}

export const GEO_KEYS = { countries: ['geo', 'countries'] as const, launchedCountries: ['geo', 'countries', 'launched'] as const }

const DAY = 24 * 60 * 60 * 1000

/** Static enough to keep for a day. */
export function useCountries() {
  return useQuery({ queryKey: GEO_KEYS.countries, queryFn: () => geoApi.countries(), staleTime: DAY, gcTime: DAY })
}

/** The sign-in picker's list: launched countries only (LP-923). With none launched (launch not configured yet) it is every
 * country, as the backend then refuses no sign-up either; an empty picker would stop everyone signing in. */
export function useLoginCountries() {
  return useQuery({
    queryKey: GEO_KEYS.launchedCountries,
    queryFn: async () => {
      const launched = await geoApi.countries({ launched: true })
      return launched.length > 0 ? launched : geoApi.countries()
    },
    staleTime: DAY,
    gcTime: DAY,
  })
}
